import { inject, Injectable, PLATFORM_ID, DestroyRef } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { CookieUtil } from '@utils/cookies';
import {
  ClarityTrackedCookieDefinition,
  ClarityTrackedCookieKey,
  ClarityUserSearchTags,
  ClarityUserTagKey,
} from '@interface/clarity-user-context.interface';

type ClarityCommand = 'identify' | 'set';
type ClarityQueueArgs = [ClarityCommand, ...unknown[]];
type ClarityClient = ((command: ClarityCommand, ...args: unknown[]) => void) & {
  q?: ClarityQueueArgs[];
};

interface ClarityWindow extends Window {
  clarity?: ClarityClient;
}

@Injectable({
  providedIn: 'root',
})
export class ClarityUserContextService {
  private readonly platformId: Object = inject(PLATFORM_ID);
  private readonly router: Router = inject(Router);
  private readonly cookies: CookieUtil = inject(CookieUtil);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly trackedCookies: readonly ClarityTrackedCookieDefinition[] = [
    { cookieKey: 'user_id', tagKey: 'user_id' },
    { cookieKey: 'id_process', tagKey: 'id_process' },
    { cookieKey: 'phone-full', tagKey: 'phone-full' },
  ];

  private readonly lastSyncedValues = new Map<ClarityUserTagKey, string>();
  private started = false;
  private lastIdentifiedUserId: string | null = null;

  /**
   * Inicializa la sincronización entre cookies del portal y etiquetas de Clarity.
   * Se ejecuta una vez y vuelve a evaluar los valores en cada cambio de ruta.
   */
  start(): void {
    if (!isPlatformBrowser(this.platformId) || this.started) {
      return;
    }

    this.started = true;
    this.syncUserContext();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.syncUserContext();
      });
  }

  private syncUserContext(): void {
    const availableTags = this.getAvailableTags();
    const tagKeys = Object.keys(availableTags) as ClarityUserTagKey[];

    if (tagKeys.length === 0) {
      return;
    }

    const clarity = this.getOrCreateClarityClient();
    this.syncTags(clarity, availableTags, tagKeys);
    this.syncIdentify(clarity, availableTags.user_id);
  }

  private getAvailableTags(): ClarityUserSearchTags {
    const tags: ClarityUserSearchTags = {};

    this.trackedCookies.forEach(({ cookieKey, tagKey }) => {
      const cookieValue = this.getCookieValue(cookieKey);
      if (!cookieValue) {
        return;
      }

      tags[tagKey] = cookieValue;
    });

    return tags;
  }

  private getCookieValue(cookieKey: ClarityTrackedCookieKey): string | null {
    if (!this.cookies.check(cookieKey)) {
      return null;
    }

    const value = this.normalizeCookieValue(this.cookies.get(cookieKey));
    return value || null;
  }

  private normalizeCookieValue(value: string | null): string {
    return (value || '').trim();
  }

  private syncTags(clarity: ClarityClient, tags: ClarityUserSearchTags, keys: ClarityUserTagKey[]): void {
    keys.forEach((tagKey) => {
      const tagValue = tags[tagKey];
      if (!tagValue) {
        return;
      }

      if (this.lastSyncedValues.get(tagKey) === tagValue) {
        return;
      }

      clarity('set', tagKey, tagValue);
      this.lastSyncedValues.set(tagKey, tagValue);
    });
  }

  private syncIdentify(clarity: ClarityClient, userId?: string): void {
    if (!userId || this.lastIdentifiedUserId === userId) {
      return;
    }

    clarity('identify', userId);
    this.lastIdentifiedUserId = userId;
  }

  /**
   * Crea una cola local compatible con Clarity cuando el script aún no está disponible.
   * Así evitamos perder etiquetas en la navegación temprana de la sesión.
   */
  private getOrCreateClarityClient(): ClarityClient {
    const clarityWindow = window as ClarityWindow;
    if (typeof clarityWindow.clarity === 'function') {
      return clarityWindow.clarity;
    }

    const queueClient: ClarityClient = ((command: ClarityCommand, ...args: unknown[]) => {
      queueClient.q = queueClient.q || [];
      queueClient.q.push([command, ...args]);
    }) as ClarityClient;

    clarityWindow.clarity = queueClient;
    return queueClient;
  }
}
